import type {
  ShipmentEventType,
  ShipmentStage,
} from "../features/shipments/types";
import { trackFeatureUsage } from "../utils/analytics";

/**
 * Envios: alta, avance de etapa y abandono.
 *
 * El avance de etapa se puede hacer por dos caminos distintos (arrastrar la
 * tarjeta en el kanban o guardar desde el detalle) y hoy no se puede saber
 * cual usa la gente ni cual falla; por eso todos los eventos llevan `method`.
 *
 * Privacidad: el envio es el objeto con mas datos del cliente de toda la app
 * (nombre, correo, telefono, direcciones de origen y destino, numero de guia,
 * coordenadas, notas). Nada de eso sale. Viajan etapas, tipos de evento,
 * conteos y banderas.
 */

export type StageChangeMethod = "kanban_drag" | "detail_form";

const FEATURE = "shipments";

/**
 * Envio creado.
 *
 * `trackingMode` (por unidad contra por viaje) es la decision de producto que
 * hoy no se puede medir: define si el envio se sigue con GPS propio o con
 * eventos manuales.
 */
export const trackShipmentCreated = (params: {
  trackingMode: string;
  transportationType: string;
  hasMerchandise: boolean;
  contactCount: number;
  notificationsEnabled: boolean;
}): void => {
  void trackFeatureUsage(FEATURE, "shipment_created", {
    tracking_mode: params.trackingMode,
    transportation_type: params.transportationType,
    has_merchandise: params.hasMerchandise,
    contact_count: params.contactCount,
    notifications_enabled: params.notificationsEnabled,
  });
};

export const trackShipmentCreateFailed = (): void => {
  void trackFeatureUsage(FEATURE, "shipment_create_failed", {});
};

/**
 * El formulario de alta se freno en una validacion del cliente.
 *
 * `field` es el nombre del campo, no su contenido. Es la senal mas directa de
 * donde abandona la gente al dar de alta un envio.
 */
export const trackShipmentFormValidationFailed = (field: string): void => {
  void trackFeatureUsage(FEATURE, "shipment_form_validation_failed", {
    field,
  });
};

/**
 * Avance (o retroceso) de etapa.
 *
 * `isBackwards` marca las correcciones: un pico ahi significa que la
 * operacion se equivoca de etapa, no que el envio realmente regreso.
 */
export const trackShipmentStageChanged = (params: {
  // Tipados a proposito, y no `string`: `to_stage` y `event_type` son dos
  // vocabularios distintos (4 etapas vs 8 tipos de evento) y con `string`
  // pelado el compilador dejaba pasar uno donde iba el otro. Paso.
  fromStage: ShipmentStage;
  toStage: ShipmentStage;
  eventType: ShipmentEventType;
  method: StageChangeMethod;
  isBackwards: boolean;
  /**
   * SOLO adjuntos subidos por la persona. Antes el kanban mandaba aqui "tenia
   * coordenadas GPS", que le sale gratis al arrastrar, y el detalle mandaba
   * "adjunto una foto": la misma propiedad medía dos cosas y el kanban salia
   * ~100% evidenciado contra ~0% del detalle.
   */
  hasEvidence: boolean;
}): void => {
  void trackFeatureUsage(FEATURE, "shipment_stage_changed", {
    from_stage: params.fromStage,
    to_stage: params.toStage,
    event_type: params.eventType,
    method: params.method,
    is_backwards: params.isBackwards,
    has_evidence: params.hasEvidence,
  });
};

export const trackShipmentStageChangeFailed = (params: {
  toStage: ShipmentStage;
  method: StageChangeMethod;
}): void => {
  void trackFeatureUsage(FEATURE, "shipment_stage_change_failed", {
    to_stage: params.toStage,
    method: params.method,
  });
};

/**
 * El usuario abrio el dialogo de confirmacion de retroceso y dijo que no.
 *
 * Mide cuantos retrocesos son un error de arrastre: si casi todos se
 * cancelan, el problema esta en la zona de suelta del kanban.
 */
export const trackShipmentStageChangeAbandoned = (params: {
  toStage: ShipmentStage;
  method: StageChangeMethod;
}): void => {
  void trackFeatureUsage(FEATURE, "shipment_stage_change_abandoned", {
    to_stage: params.toStage,
    method: params.method,
  });
};

export const trackShipmentDeleted = (stage: ShipmentStage): void => {
  void trackFeatureUsage(FEATURE, "shipment_deleted", { stage });
};

/**
 * Liga publica de rastreo renovada.
 *
 * Nunca viaja la URL: lleva un token de acceso publico al envio.
 */
export const trackTrackingLinkRenewed = (): void => {
  void trackFeatureUsage(FEATURE, "tracking_link_renewed", {});
};
